import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Copy, Download, ExternalLink, Plus, Search, CheckCircle2, AlertTriangle, XCircle, Loader2, Info } from 'lucide-react';
import { AddressAutocomplete, StructuredAddress } from '@/components/AddressAutocomplete';

// South-African cities (major metros + secondary — used for the broker
// creation dropdown. Ordered by rough size so common picks appear first).
const SA_CITIES = [
  'Johannesburg', 'Cape Town', 'Durban', 'Pretoria', 'Port Elizabeth', 'Gqeberha',
  'Bloemfontein', 'East London', 'Pietermaritzburg', 'Polokwane', 'Nelspruit', 'Mbombela',
  'Kimberley', 'Rustenburg', 'George', 'Stellenbosch', 'Paarl', 'Somerset West',
  'Sandton', 'Randburg', 'Roodepoort', 'Midrand', 'Centurion', 'Kempton Park',
  'Boksburg', 'Benoni', 'Germiston', 'Krugersdorp', 'Vereeniging', 'Vanderbijlpark',
  'Sasolburg', 'Klerksdorp', 'Potchefstroom', 'Welkom', 'Emalahleni', 'Witbank',
  'Middelburg', 'Secunda', 'Ermelo', 'Newcastle', 'Ladysmith', 'Richards Bay',
  'Empangeni', 'Umhlanga', 'Ballito', 'Margate', 'Knysna', 'Mossel Bay',
  'Oudtshoorn', 'Worcester', 'Beaufort West', 'Upington', 'Kuruman', 'Kuils River',
  'Bellville', 'Milnerton', 'Table View', 'Mitchells Plain', 'Khayelitsha',
  'Alberton', 'Springs', 'Nigel', 'Heidelberg', 'Mafikeng', 'Mahikeng',
  'Vryburg', 'Lichtenburg', 'Zeerust', 'Thohoyandou', 'Tzaneen', 'Louis Trichardt',
  'Musina', 'Groblersdal', 'Bela-Bela', 'Modimolle', 'Mokopane', 'Phalaborwa',
  'Hoedspruit', 'White River', 'Barberton', 'Piet Retief', 'Standerton',
];

interface CitySelectProps {
  value: string;
  onChange: (v: string) => void;
  'data-testid'?: string;
}

const CitySelect: React.FC<CitySelectProps> = ({ value, onChange, ...rest }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = React.useMemo(() =>
    SA_CITIES.filter((c) => c.toLowerCase().includes(q.toLowerCase())).slice(0, 30),
    [q],
  );
  return (
    <div className="relative">
      <button
        type="button"
        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm text-left flex items-center justify-between hover:border-raptor-blue focus:outline-none focus:ring-2 focus:ring-raptor-blue/40"
        onClick={() => setOpen(!open)}
        data-testid={rest['data-testid']}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={value ? 'text-raptor-navy' : 'text-raptor-navy/40'}>{value || 'Select city…'}</span>
        <Search size={14} className="text-raptor-navy/40" />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-20 rounded-md border border-border/60 bg-white shadow-lg">
          <div className="p-2 border-b border-border/60">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search cities…" className="h-8" autoFocus />
          </div>
          <ul role="listbox" className="max-h-56 overflow-y-auto py-1 text-sm">
            {filtered.length === 0 && <li className="px-3 py-2 text-raptor-navy/50 font-body">No matches — type a custom city name and press Enter.</li>}
            {filtered.map((c) => (
              <li
                key={c}
                role="option"
                aria-selected={value === c}
                className={`px-3 py-1.5 cursor-pointer hover:bg-raptor-blue/10 ${value === c ? 'bg-raptor-blue/15 text-raptor-navy font-medium' : 'text-raptor-navy/80'}`}
                onClick={() => { onChange(c); setOpen(false); setQ(''); }}
              >{c}</li>
            ))}
            {q && !SA_CITIES.some((c) => c.toLowerCase() === q.toLowerCase()) && (
              <li
                role="option"
                className="px-3 py-1.5 cursor-pointer hover:bg-raptor-blue/10 text-raptor-blue font-medium border-t border-border/40"
                onClick={() => { onChange(q); setOpen(false); setQ(''); }}
              >Use custom city: <span className="font-tabular">&ldquo;{q}&rdquo;</span></li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

interface Broker {
  id: string; brokerage_name: string; fsp_number: string; city: string;
  survey_access: boolean; active: boolean;
  registration_link: string; number_of_staff: number; number_registered: number; created_at: string;
}

export const BrokersPage: React.FC = () => {
  const [items, setItems] = useState<Broker[]>([]);
  const [creating, setCreating] = useState(false);
  const emptyForm: {
    brokerage_name: string;
    fsp_number: string;
    city: string;
    address: StructuredAddress | null;
    survey_access: boolean;
    primary_contact_name: string;
    primary_contact_phone: string;
    primary_contact_email: string;
  } = {
    brokerage_name: '', fsp_number: '', city: '', address: null, survey_access: false,
    primary_contact_name: '', primary_contact_phone: '', primary_contact_email: '',
  };
  const [form, setForm] = useState(emptyForm);
  // Bumped each time the create panel is opened so the whole form remounts
  // blank — otherwise child inputs (e.g. the address autocomplete) can keep
  // the previous broker's text even after state is reset.
  const [formNonce, setFormNonce] = useState(0);
  const nav = useNavigate();

  // ---------- FSP soft-validation state ----------
  // We call /api/brokers/validate-fsp ~500ms after the user stops
  // typing so they see immediate feedback WITHOUT triggering a lookup
  // on every keystroke. The result is soft/advisory — we never block
  // submission from the front-end; the backend enforces uniqueness.
  interface FspStatus {
    ok: boolean;
    fsca_status: 'verified' | 'duplicate' | 'invalid_format' | 'unverified' | string;
    provider_name?: string | null;
    error?: string;
    warning?: string;
    verify_url?: string;
  }
  const [fspLookup, setFspLookup] = useState<{ loading: boolean; status: FspStatus | null; forNumber: string }>({
    loading: false, status: null, forNumber: '',
  });

  useEffect(() => {
    const raw = form.fsp_number.trim();
    if (!raw) { setFspLookup({ loading: false, status: null, forNumber: '' }); return; }
    // Debounce the call so we hit the backend at most once per pause.
    const handle = setTimeout(() => {
      setFspLookup((s) => ({ ...s, loading: true, forNumber: raw }));
      api.post<FspStatus>('/brokers/validate-fsp', { fsp_number: raw })
        .then((r) => setFspLookup({ loading: false, status: r.data, forNumber: raw }))
        .catch((err: unknown) => {
          // The endpoint deliberately returns 200 with ok:false for
          // duplicate/invalid_format cases, but 400 is possible on
          // schema errors. Show the message either way.
          const detail = (err as { response?: { data?: FspStatus | { detail?: string } } })?.response?.data;
          const fallback: FspStatus = (detail as FspStatus)?.fsca_status
            ? (detail as FspStatus)
            : { ok: false, fsca_status: 'unverified', error: 'Could not validate — please try again.' };
          setFspLookup({ loading: false, status: fallback, forNumber: raw });
        });
    }, 500);
    return () => clearTimeout(handle);
  }, [form.fsp_number]);

  const load = async (): Promise<void> => { const r = await api.get<Broker[]>('/brokers'); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const copyLink = async (url: string): Promise<void> => { await navigator.clipboard.writeText(url); toast.success('Registration link copied.'); };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!form.primary_contact_phone && !form.primary_contact_email) {
      toast.error('Add a primary contact phone or email so the broker can register.');
      return;
    }
    try {
      await api.post('/brokers', form);
      toast.success('Broker created — primary contact whitelisted for registration');
      setCreating(false);
      setForm(emptyForm);
      load();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Create failed');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">Brokers</h2>
          <p className="text-sm text-raptor-navy/60 font-body">Manage brokerages and their approved staff lists.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/exports/brokers.xlsx`, '_blank')}>
            <Download size={16} /> Export XLSX
          </Button>
          <Button data-testid="broker-create-toggle" variant="gradient" size="sm" onClick={() => {
            if (creating) { setCreating(false); return; }
            // Opening a fresh "New broker" form — always start blank.
            setForm(emptyForm);
            setFspLookup({ loading: false, status: null, forNumber: '' });
            setFormNonce((n) => n + 1);
            setCreating(true);
          }}>
            <Plus size={16} /> {creating ? 'Cancel' : 'New broker'}
          </Button>
        </div>
      </div>

      {creating && (
        <Card>
          <CardHeader><CardTitle>Create broker</CardTitle></CardHeader>
          <CardContent>
            <form key={formNonce} onSubmit={submit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                {/* ─── 1. Identity ────────────────────────────── */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label>FSP number *</Label>
                    {form.fsp_number.trim() && (
                      <a
                        href={`https://www.fsca.co.za/Fais/Search_FSP.htm?fspNo=${encodeURIComponent(form.fsp_number.trim())}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-raptor-blue hover:underline"
                        data-testid="fsca-verify-link"
                      >
                        Verify on FSCA &rarr;
                      </a>
                    )}
                  </div>
                  <Input data-testid="broker-fsp" required value={form.fsp_number} onChange={(e) => setForm({ ...form, fsp_number: e.target.value })} placeholder="e.g. 12345" autoFocus />
                  <FspStatusHint loading={fspLookup.loading} status={fspLookup.status} forNumber={fspLookup.forNumber} typedNumber={form.fsp_number.trim()} />
                </div>
                <div className="space-y-1.5"><Label>Brokerage name *</Label>
                  <Input data-testid="broker-name" required value={form.brokerage_name} onChange={(e) => setForm({ ...form, brokerage_name: e.target.value })} />
                </div>

                {/* ─── 2. Location (address first, drives autofill) ── */}
                <div className="md:col-span-2 pt-1 border-t border-border/40 mt-1">
                  <div className="text-[10px] uppercase tracking-wide text-raptor-navy/60 font-body mb-2 mt-2">Location</div>
                  <Label>Physical address *</Label>
                  <AddressAutocomplete
                    testid="broker-address"
                    value={form.address}
                    onChange={(addr: StructuredAddress | null) => setForm({
                      // Mirror the picked address's city/province/postal
                      // into the top-level fields so filters and the
                      // brokerage list can search by territory without
                      // digging into the nested address object.
                      ...form,
                      address: addr,
                      city: addr?.city || form.city,
                    })}
                    placeholder="Start typing to auto-fill city, province, postal code and GPS…"
                  />
                  {form.address?.lat != null && form.address?.lng != null && (
                    <div className="mt-1 text-[11px] text-emerald-700 font-tabular">
                      ✓ Coords captured: {form.address.lat.toFixed(5)}, {form.address.lng.toFixed(5)}
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>City / Town</Label>
                  <Input
                    data-testid="broker-city"
                    value={form.address?.city || form.city}
                    onChange={(e) => setForm({
                      ...form,
                      city: e.target.value,
                      address: form.address ? { ...form.address, city: e.target.value } : { formatted: '', city: e.target.value },
                    })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Province</Label>
                  <Input
                    data-testid="broker-province"
                    value={form.address?.province || ''}
                    onChange={(e) => setForm({
                      ...form,
                      address: form.address ? { ...form.address, province: e.target.value } : { formatted: '', province: e.target.value },
                    })}
                  />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Postal code</Label>
                  <Input
                    data-testid="broker-postal"
                    value={form.address?.postal_code || ''}
                    onChange={(e) => setForm({
                      ...form,
                      address: form.address ? { ...form.address, postal_code: e.target.value } : { formatted: '', postal_code: e.target.value },
                    })}
                  />
                </div>

                {/* ─── 3. Access / portal ─────────────────────── */}
                <div className="space-y-1.5 md:col-span-2 pt-1 border-t border-border/40 mt-1">
                  <div className="text-[10px] uppercase tracking-wide text-raptor-navy/60 font-body mb-2 mt-2">Portal access</div>
                  <div className="flex items-center gap-1.5">
                    <Label>Survey results access</Label>
                    <InfoPopover
                      label="What does this mean?"
                      testid="survey-access-info"
                      content={
                        <>
                          <div className="font-semibold text-raptor-navy mb-1.5">Controls what this broker sees on their portal:</div>
                          <div className="mb-1.5">
                            <span className="font-semibold">No — status only (default):</span>{' '}
                            The broker sees that a survey exists and its status (issued / in progress / submitted), plus basic metadata (site reference, date). They <b>cannot</b> see the answers, photos, or download the PDF. Recommended for tenant-facing sales brokers.
                          </div>
                          <div>
                            <span className="font-semibold">Yes — full survey results:</span>{' '}
                            The broker can view the complete survey — every answer, every photo, and can export the PDF report. Use this for brokers who need to action findings (e.g. remediation, quoting).
                          </div>
                          <div className="mt-2 pt-2 border-t border-border/40 text-[11px] text-raptor-navy/60">
                            Can be changed later on the broker&rsquo;s detail page.
                          </div>
                        </>
                      }
                    />
                  </div>
                  <Select data-testid="broker-survey-access" value={form.survey_access ? '1' : '0'} onChange={(e) => setForm({ ...form, survey_access: e.target.value === '1' })}>
                    <option value="0">No — status only (default)</option>
                    <option value="1">Yes — full survey results including PDF exports</option>
                  </Select>
                </div>
              </div>

              <div className="rounded-md border border-raptor-blue/30 bg-raptor-blue/5 p-3">
                <div className="text-sm font-semibold text-raptor-navy mb-1">Primary contact</div>
                <div className="text-xs text-raptor-navy/70 font-body mb-3">
                  The person who will click the registration link first. Their phone / email is added to the approved whitelist automatically so their OTP works out of the box. Enter <b>at least one</b> of phone or email.
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="space-y-1.5"><Label>Full name</Label>
                    <Input data-testid="broker-primary-name" value={form.primary_contact_name} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} placeholder="e.g. Jane Doe" />
                  </div>
                  <div className="space-y-1.5"><Label>Mobile *</Label>
                    <Input data-testid="broker-primary-phone" value={form.primary_contact_phone} onChange={(e) => setForm({ ...form, primary_contact_phone: e.target.value })} placeholder="0821234567 or +27821234567" />
                  </div>
                  <div className="space-y-1.5"><Label>Email</Label>
                    <Input data-testid="broker-primary-email" type="email" value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} placeholder="jane@brokerage.co.za" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button data-testid="broker-create-submit" type="submit" variant="gradient">Create broker</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm font-body">
            <thead className="bg-raptor-neutral/40">
              <tr className="text-left text-raptor-navy/70 text-xs uppercase tracking-wide">
                <th className="py-3 px-4">ID</th>
                <th className="py-3 px-4">Brokerage</th>
                <th className="py-3 px-4">FSP</th>
                <th className="py-3 px-4">City</th>
                <th className="py-3 px-4">Staff</th>
                <th className="py-3 px-4">Survey access</th>
                <th className="py-3 px-4">Registration link</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-raptor-navy/50 font-body">No brokers yet. Click <b>New broker</b> to add one.</td></tr>
              )}
              {items.map((b) => (
                <tr key={b.id} className="border-t border-border/60 hover:bg-raptor-neutral/20 cursor-pointer"
                  onClick={() => nav(`/insurer/brokers/${b.id}`)} data-testid={`broker-row-${b.id}`}>
                  <td className="py-3 px-4 font-tabular text-raptor-navy">{b.id}</td>
                  <td className="py-3 px-4 font-semibold">{b.brokerage_name}</td>
                  <td className="py-3 px-4 font-tabular">{b.fsp_number}</td>
                  <td className="py-3 px-4">{b.city}</td>
                  <td className="py-3 px-4 font-tabular">{b.number_registered} / {b.number_of_staff}</td>
                  <td className="py-3 px-4">
                    {b.survey_access ? <Badge variant="success">Full results</Badge> : <Badge variant="muted">Status only</Badge>}
                  </td>
                  <td className="py-3 px-4">
                    <button data-testid={`broker-copy-link-${b.id}`} onClick={(e) => { e.stopPropagation(); copyLink(b.registration_link); }} className="inline-flex items-center gap-1 text-raptor-blue hover:underline text-xs font-tabular">
                      <Copy size={12} /> Copy link
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Lightweight self-contained info popover. Zero external UI dependencies —
// pure Tailwind + React state. Used for the "What does this mean?" hints
// next to form labels. Opens on hover or on click (for touch devices);
// dismisses on outside click / Esc / blur.
// ---------------------------------------------------------------------------
const InfoPopover: React.FC<{
  label?: string;
  testid?: string;
  content: React.ReactNode;
}> = ({ label = 'More info', testid, content }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        aria-label={label}
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="text-raptor-navy/40 hover:text-raptor-blue transition-colors inline-flex align-middle"
      >
        <Info size={13} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-6 top-1/2 -translate-y-1/2 z-50 w-80 rounded-md border border-border/60 bg-white shadow-lg p-3 text-xs font-body leading-relaxed text-raptor-navy"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          {content}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Soft FSP validation hint — rendered directly beneath the FSP input in the
// broker creation form. Never disables the submit button; the operator is
// always in control. Colour + icon reflect the FSCA response tier:
//
//   verified       → green   (FSCA register hit + name returned)
//   unverified     → amber   (format ok + FSCA unreachable; verify manually)
//   duplicate      → red     (already registered — shows which brokerage)
//   invalid_format → red     (format failure — e.g. non-digits, >6 digits)
//
// The message is intentionally informational; the backend still enforces
// uniqueness/format on POST /brokers, so a determined user submitting a
// duplicate still gets a hard error at submit time.
// ---------------------------------------------------------------------------
interface FspStatusPayload {
  ok: boolean;
  fsca_status: string;
  provider_name?: string | null;
  error?: string;
  warning?: string;
  verify_url?: string;
}

const FspStatusHint: React.FC<{
  loading: boolean;
  status: FspStatusPayload | null;
  forNumber: string;
  typedNumber: string;
}> = ({ loading, status, forNumber, typedNumber }) => {
  // While the debounce timer is still counting down for a *different*
  // number than the one we last got a response for, render a subtle
  // "typing…" placeholder so the operator knows validation is coming.
  const staleResponse = !!status && forNumber !== typedNumber;

  if (loading || (typedNumber && !status && !loading) || (typedNumber && staleResponse)) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-raptor-navy/50 font-body pl-0.5 pt-0.5" data-testid="fsp-status-hint">
        <Loader2 size={12} className="animate-spin" />
        Checking FSCA register…
      </div>
    );
  }
  if (!status || !typedNumber) return null;

  const s = status.fsca_status;
  const commonClasses = 'flex items-start gap-1.5 text-xs font-body pl-0.5 pt-1';

  if (s === 'verified') {
    return (
      <div className={`${commonClasses} text-emerald-700`} data-testid="fsp-status-hint">
        <CheckCircle2 size={13} className="shrink-0 mt-0.5" />
        <span>
          Valid FSP{status.provider_name ? <> — associated with <b>{status.provider_name}</b></> : ''}
          {' '}(FSCA register).
        </span>
      </div>
    );
  }
  if (s === 'duplicate') {
    return (
      <div className={`${commonClasses} text-red-700`} data-testid="fsp-status-hint">
        <XCircle size={13} className="shrink-0 mt-0.5" />
        <span>{status.error || 'FSP number already registered in this tenant.'}</span>
      </div>
    );
  }
  if (s === 'invalid_format') {
    return (
      <div className={`${commonClasses} text-red-700`} data-testid="fsp-status-hint">
        <XCircle size={13} className="shrink-0 mt-0.5" />
        <span>{status.error || 'FSP number must be a 1–6 digit integer.'}</span>
      </div>
    );
  }
  if (s === 'unverified') {
    return (
      <div className={`${commonClasses} text-amber-700`} data-testid="fsp-status-hint">
        <AlertTriangle size={13} className="shrink-0 mt-0.5" />
        <span>
          Format valid, but the FSCA public register could not be reached automatically.{' '}
          {status.verify_url && (
            <a
              href={status.verify_url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-dotted hover:text-raptor-blue inline-flex items-center gap-0.5"
              data-testid="fsp-manual-verify-link"
            >
              Verify manually <ExternalLink size={10} />
            </a>
          )}
        </span>
      </div>
    );
  }

  // Fallback — unknown status
  return (
    <div className={`${commonClasses} text-raptor-navy/60`} data-testid="fsp-status-hint">
      <Info size={13} className="shrink-0 mt-0.5" />
      <span>{status.error || status.warning || 'Unknown FSP status.'}</span>
    </div>
  );
};

